import type { ObservationRecord } from '../../types/observations';
import type { QuarterlyReportSummary } from '../../types/quarterlyReport';
import type { SurveillancePlanRecord } from '../../types/surveillance';
import type { PublishedFamilyTreeRecord } from '../../types/familyTree';
import { coerceDate, formatDateLabel } from '../dateLabels';
import { type SourceStatus, isUsable } from './types';

/**
 * "Recent activity" — one reverse-chronological stream of what has happened on
 * the member's record.
 *
 * The collections that look purpose-built for this — `audit_events` and
 * `clinician_interactions` — are staff-only, and deliberately so: they are
 * append-only audit logs with client writes denied for everyone. So this merges
 * the member-readable streams instead: their own observations, released
 * reports, plan sign-offs, and a published family tree.
 *
 * `buildPlanActivity` in memberEngagement is not reused. It caps at six, covers
 * only plan and family-tree events, and formats its dates into strings while
 * discarding the sort key — so it cannot be merged with anything else.
 *
 * Observations are grouped by day. A blood panel arrives as twenty separate
 * documents; twenty identical "result added" rows would bury everything else.
 */

export type ActivityIcon = 'flask' | 'pulse' | 'doc' | 'history' | 'apple';

export interface ActivityEntry {
  id: string;
  at: Date;
  dateLabel: string;
  title: string;
  detail?: string;
  icon: ActivityIcon;
  href?: string;
}

export interface DashboardActivityInput {
  labObservations: ObservationRecord[];
  labsStatus: SourceStatus;
  wearableObservations: ObservationRecord[];
  wearableStatus: SourceStatus;
  releasedReports: QuarterlyReportSummary[];
  reportsStatus: SourceStatus;
  plan: SurveillancePlanRecord | null;
  planStatus: SourceStatus;
  familyTree: PublishedFamilyTreeRecord | null;
  limit?: number;
}

const DEFAULT_LIMIT = 8;

const dayKey = (date: Date) => date.toISOString().slice(0, 10);

function groupObservationsByDay(observations: ObservationRecord[]): Map<string, Date> {
  const days = new Map<string, Date>();
  for (const observation of observations) {
    const at = coerceDate(observation.observedAt || observation.createdAt);
    if (!at) continue;
    const key = dayKey(at);
    const existing = days.get(key);
    if (!existing || at.getTime() > existing.getTime()) days.set(key, at);
  }
  return days;
}

function countByDay(observations: ObservationRecord[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const observation of observations) {
    const at = coerceDate(observation.observedAt || observation.createdAt);
    if (!at) continue;
    const key = dayKey(at);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return counts;
}

export function buildDashboardActivity(input: DashboardActivityInput): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  if (isUsable(input.labsStatus)) {
    const counts = countByDay(input.labObservations);
    for (const [key, at] of groupObservationsByDay(input.labObservations)) {
      const count = counts.get(key) || 1;
      entries.push({
        id: `labs-${key}`,
        at,
        dateLabel: formatDateLabel(at),
        title: count === 1 ? 'Lab result added' : `${count} lab results added`,
        icon: 'flask',
        href: '/intelligence?tab=uploads',
      });
    }
  }

  if (isUsable(input.wearableStatus)) {
    for (const [key, at] of groupObservationsByDay(input.wearableObservations)) {
      entries.push({
        id: `wearable-${key}`,
        at,
        dateLabel: formatDateLabel(at),
        title: 'Wearable data synced',
        icon: 'pulse',
        href: '/intelligence',
      });
    }
  }

  if (isUsable(input.reportsStatus)) {
    for (const report of input.releasedReports) {
      const at = coerceDate(report.releasedAt);
      if (!at) continue;
      entries.push({
        id: `report-${report.id}`,
        at,
        dateLabel: formatDateLabel(at),
        title: `${report.quarterLabel} report released`,
        detail: 'Reviewed and signed off by a clinician',
        icon: 'doc',
        href: `/reports/${report.id}`,
      });
    }
  }

  if (isUsable(input.planStatus) && input.plan) {
    const signedOff = coerceDate(input.plan.signedOffAt);
    if (signedOff) {
      entries.push({
        id: `plan-signed-${input.plan.id}`,
        at: signedOff,
        dateLabel: formatDateLabel(signedOff),
        title: 'Your plan was updated',
        detail: input.plan.clinicianName ? `Signed off by ${input.plan.clinicianName}` : undefined,
        icon: 'doc',
        href: '/plan',
      });
    }
  }

  const treePublished = coerceDate(input.familyTree?.publishedAt);
  if (treePublished) {
    entries.push({
      id: 'family-tree-published',
      at: treePublished,
      dateLabel: formatDateLabel(treePublished),
      title: 'Your family history was updated',
      icon: 'history',
      href: '/history',
    });
  }

  return entries
    .sort((left, right) => right.at.getTime() - left.at.getTime())
    .slice(0, input.limit ?? DEFAULT_LIMIT);
}
